import 'leaflet/dist/leaflet.css';
import { useEffect } from 'react';
import {
  MapContainer,
  TileLayer,
  WMSTileLayer,
  CircleMarker,
  Circle,
  Polyline,
  Popup,
  useMap,
} from 'react-leaflet';
import type { LatLngBoundsExpression } from 'leaflet';
import type { NAGPSObject } from '@/api/models/gps.types';
import type { NAGeofenceObject } from '@/api/models/sensor.types';
import {
  formatCoordinate,
  formatSpeed,
  formatDateTime,
  formatTimeAgo,
  feetToMeters,
  formatFeet,
} from '@/lib/formatters';

// Track line colors — mirror TrackingMapPage
const TRACK_COLORS = [
  '#06B6D4', '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981',
];

// ── Imperative map view helpers ──────────────────────────────────────────

function RecenterMap({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();
  useEffect(() => {
    map.setView([lat, lng]);
  }, [map, lat, lng]);
  return null;
}

function FitBounds({ points }: { points: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 14);
      return;
    }
    const bounds: LatLngBoundsExpression = points;
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [map, points]);
  return null;
}

// ── Props ─────────────────────────────────────────────────────────────────

export interface ChartMapViewProps {
  viewMode: 'live' | 'routes';
  currentGPS: NAGPSObject | null | undefined;
  vesselName: string;
  deviceGeofences: NAGeofenceObject[];
  selectedRouteIdx: number | null;
  activeTrackPath: { lat: number; lng: number }[];
  activeTrackValid: NAGPSObject[];
  allTrackPaths: { lat: number; lng: number }[][];
  defaultCenter: { lat: number; lng: number };
}

// ── Main component ────────────────────────────────────────────────────────

export function ChartMapView({
  viewMode,
  currentGPS,
  vesselName,
  deviceGeofences,
  selectedRouteIdx,
  activeTrackPath,
  activeTrackValid,
  allTrackPaths,
  defaultCenter,
}: ChartMapViewProps) {

  const center: [number, number] = [defaultCenter.lat, defaultCenter.lng];

  // Points used to fit bounds in routes mode
  const fitPoints: [number, number][] = viewMode === 'routes'
    ? (selectedRouteIdx !== null
      ? activeTrackPath.map((p) => [p.lat, p.lng])
      : allTrackPaths.flat().map((p) => [p.lat, p.lng]))
    : [];

  return (
    <MapContainer
      center={center}
      zoom={14}
      style={{ height: '100%', width: '100%' }}
      attributionControl={true}
    >
      {/* OSM base layer */}
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        maxZoom={19}
      />

      {/* NOAA Chart Display via WMS — direct, no auth required */}
      <WMSTileLayer
        url="https://gis.charttools.noaa.gov/arcgis/rest/services/MCS/NOAAChartDisplay/MapServer/exts/MaritimeChartService/WMSServer"
        layers="0,1,2,3,4,5,6,7"
        format="image/png"
        transparent={true}
        version="1.3.0"
        opacity={0.85}
        attribution='Chart data: <a href="https://www.nauticalcharts.noaa.gov">NOAA ENC</a>'
      />

      {/* Auto-center imperative helpers */}
      {viewMode === 'live' && currentGPS?.locationValid && (
        <RecenterMap lat={currentGPS.latitude} lng={currentGPS.longitude} />
      )}
      {viewMode === 'routes' && fitPoints.length > 0 && (
        <FitBounds points={fitPoints} />
      )}

      {/* ── Geofence circles ─────────────────────────────────────────── */}
      {deviceGeofences.map((gf, idx) => gf.enabled && (
        <Circle
          key={`gf-${idx}`}
          center={[gf.refLat, gf.refLon]}
          radius={feetToMeters(gf.radius)}
          pathOptions={{
            color: gf.alertPresent ? '#EF4444' : '#22C55E',
            fillColor: gf.alertPresent ? '#EF4444' : '#22C55E',
            fillOpacity: 0.1,
            weight: 2,
            opacity: 0.7,
          }}
        />
      ))}

      {/* ── Route polylines ──────────────────────────────────────────── */}
      {viewMode === 'routes' && selectedRouteIdx !== null && activeTrackPath.length >= 2 && (
        <Polyline
          positions={activeTrackPath.map((p) => [p.lat, p.lng])}
          pathOptions={{
            color: TRACK_COLORS[selectedRouteIdx % TRACK_COLORS.length],
            weight: 4,
            opacity: 0.85,
          }}
        />
      )}

      {viewMode === 'routes' && selectedRouteIdx === null && allTrackPaths.map((path, i) =>
        path.length >= 2 && (
          <Polyline
            key={`all-${i}`}
            positions={path.map((p) => [p.lat, p.lng])}
            pathOptions={{
              color: TRACK_COLORS[i % TRACK_COLORS.length],
              weight: 3,
              opacity: 0.85,
            }}
          />
        ),
      )}

      {/* ── Track point markers (selected route only) ─────────────────── */}
      {viewMode === 'routes' && selectedRouteIdx !== null && activeTrackValid.map((pt, i) => (
        <CircleMarker
          key={`pt-${i}`}
          center={[pt.latitude, pt.longitude]}
          radius={5}
          pathOptions={{
            color: '#fff',
            fillColor: '#06B6D4',
            fillOpacity: 1,
            weight: 1.5,
          }}
        >
          <Popup>
            <div className="min-w-[150px] space-y-0.5 text-xs">
              <p className="font-semibold text-slate-800">Waypoint {i + 1}</p>
              <p>{formatDateTime(pt.updatedDT || pt.createdDT)}</p>
              <p>{formatCoordinate(pt.latitude, pt.longitude)}</p>
              {pt.speed != null && <p>Speed: {formatSpeed(pt.speed)}</p>}
              {pt.heading != null && pt.heading > 0 && <p>Heading: {pt.heading}&deg;</p>}
              {pt.totalExpectedErrorFt != null && pt.totalExpectedErrorFt > 0 && (
                <p className="text-slate-500">Accuracy: &plusmn;{formatFeet(pt.totalExpectedErrorFt)}</p>
              )}
            </div>
          </Popup>
        </CircleMarker>
      ))}

      {/* ── Current vessel position ───────────────────────────────────── */}
      {currentGPS?.locationValid && (
        <>
          {/* Accuracy circle */}
          {currentGPS.totalExpectedErrorFt != null && currentGPS.totalExpectedErrorFt > 0 && (
            <Circle
              center={[currentGPS.latitude, currentGPS.longitude]}
              radius={feetToMeters(currentGPS.totalExpectedErrorFt)}
              pathOptions={{
                color: '#06B6D4',
                fillColor: '#06B6D4',
                fillOpacity: 0.1,
                weight: 1.5,
                opacity: 0.4,
              }}
            />
          )}

          {/* Vessel marker */}
          <CircleMarker
            center={[currentGPS.latitude, currentGPS.longitude]}
            radius={10}
            pathOptions={{
              color: '#fff',
              fillColor: '#06B6D4',
              fillOpacity: 1,
              weight: 3,
            }}
          >
            <Popup>
              <div className="min-w-[170px] space-y-0.5 text-xs">
                <p className="font-semibold text-slate-900">{vesselName}</p>
                <p>{formatCoordinate(currentGPS.latitude, currentGPS.longitude)}</p>
                {currentGPS.speed != null && <p>Speed: {formatSpeed(currentGPS.speed)}</p>}
                {currentGPS.heading != null && currentGPS.heading > 0 && <p>Heading: {currentGPS.heading}&deg;</p>}
                {currentGPS.totalExpectedErrorFt != null && currentGPS.totalExpectedErrorFt > 0 && (
                  <p>Accuracy: &plusmn;{formatFeet(currentGPS.totalExpectedErrorFt)}</p>
                )}
                {currentGPS.updatedDT && (
                  <p className="text-slate-400">
                    {formatDateTime(currentGPS.updatedDT)} ({formatTimeAgo(currentGPS.updatedDT)})
                  </p>
                )}
              </div>
            </Popup>
          </CircleMarker>
        </>
      )}
    </MapContainer>
  );
}
